import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { EndeavorTypeApi } from '../../services/ApiService';

function EndeavorTypeMetadataEdit() {
  const { id: endeavorTypeId, metadataId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(metadataId);
  
  const [endeavorType, setEndeavorType] = useState(null);
  const [formData, setFormData] = useState({
    name: '', // UPDATED: Changed from fieldName to name
    fieldType: 'Text', // UPDATED: Default to 'Text' to match API
    description: '',
    isRequired: false,
    defaultValue: '',
    ordering: 1 // UPDATED: Changed from displayOrder to ordering
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fieldTypes = [
    { value: 'Text', label: 'Text' }, // UPDATED: Match API field types
    { value: 'Number', label: 'Number' },
    { value: 'Date', label: 'Date' },
    { value: 'DateTime', label: 'Date & Time' },
    { value: 'Boolean', label: 'Boolean (Yes/No)' },
    { value: 'Select', label: 'Select List' },
    { value: 'MultiSelect', label: 'Multi-Select' },
    { value: 'TextArea', label: 'Text Area' },
    { value: 'URL', label: 'URL' },
    { value: 'Email', label: 'Email' }
  ];

  useEffect(() => {
    loadEndeavorType();
    if (isEditing) {
      loadMetadataDefinition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endeavorTypeId, metadataId]);

  const loadEndeavorType = async () => {
    try {
      const response = await EndeavorTypeApi.getEndeavorType(endeavorTypeId);
      if (!response.ok) {
        throw new Error('Failed to load endeavor type');
      }
      const data = await response.json();
      setEndeavorType(data);
    } catch (error) {
      console.error('Error loading endeavor type:', error);
      setError('Failed to load endeavor type. Please try again.');
    }
  };

  const loadMetadataDefinition = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get all metadata definitions for this endeavor type and find the one we're editing
      const response = await EndeavorTypeApi.getEndeavorTypeMetadataDefinitions(endeavorTypeId);
      if (!response.ok) {
        throw new Error('Failed to load metadata definitions');
      }
      const relationships = await response.json();
      console.log('EndeavorTypeMetadataEdit: API response:', relationships);
      
      // UPDATED: Find the relationship by its ID
      const relationship = relationships.find(rel => rel.id === parseInt(metadataId));
      
      if (!relationship || !relationship.metadataDefinition) {
        throw new Error('Metadata definition not found');
      }
      
      const metadata = relationship.metadataDefinition;
      
      setFormData({
        name: metadata.name || '', // UPDATED: Use 'name' field
        fieldType: metadata.fieldType || 'Text',
        description: metadata.description || '',
        isRequired: relationship.isRequired || false, // From relationship
        defaultValue: metadata.defaultValue || '',
        ordering: relationship.ordering || 1 // UPDATED: Use 'ordering' field
      });
    } catch (error) {
      console.error('Error loading metadata definition:', error);
      setError('Failed to load metadata definition. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // UPDATED: Structure the data correctly for the API
      const submitData = {
        name: formData.name, // UPDATED: Use 'name' field
        fieldType: formData.fieldType,
        description: formData.description,
        defaultValue: formData.defaultValue || null,
        isRequired: formData.isRequired,
        ordering: parseInt(formData.ordering) || 1 // UPDATED: Use 'ordering' field
      };

      console.log('EndeavorTypeMetadataEdit: Submitting data:', submitData);

      const response = isEditing 
        ? await EndeavorTypeApi.updateEndeavorTypeMetadataDefinition(endeavorTypeId, metadataId, submitData)
        : await EndeavorTypeApi.addMetadataDefinitionToEndeavorType(endeavorTypeId, submitData);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('EndeavorTypeMetadataEdit: API error:', errorData);
        throw new Error(errorData?.message || 'Failed to save metadata definition');
      }

      navigate(`/settings/endeavor-types/${endeavorTypeId}/metadata`);
    } catch (error) {
      console.error('Error saving metadata definition:', error);
      setError(error.message || 'Failed to save metadata definition. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/settings/endeavor-types/${endeavorTypeId}/metadata`);
  };

  if (loading && isEditing) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading metadata definition...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div style={{ marginBottom: '1rem' }}>
        <Link 
          to={`/settings/endeavor-types/${endeavorTypeId}/metadata`} 
          style={{ color: 'var(--primary-color)', textDecoration: 'none' }}
        >
          ← Back to {endeavorType?.name || 'Endeavor Type'} Metadata
        </Link>
      </div>

      <h2>
        {isEditing ? 'Edit' : 'Add'} Metadata Field
        {endeavorType && (
          <div style={{ fontSize: '1rem', fontWeight: 'normal', color: '#666', marginTop: '0.25rem' }}>
            for {endeavorType.name}
          </div>
        )}
      </h2>
      
      {error && (
        <div className="error-message" style={{ 
          color: '#dc3545', 
          backgroundColor: 'var(--intent-danger-bg, #f8d7da)', 
          border: '1px solid #f5c6cb', 
          borderRadius: '4px', 
          padding: '0.75rem',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Field Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Enter field name (e.g., 'Course Name', 'Difficulty Level')"
          />
        </div>

        <div className="form-group">
          <label htmlFor="fieldType">Field Type *</label>
          <select
            id="fieldType"
            name="fieldType"
            value={formData.fieldType}
            onChange={handleInputChange}
            required
            disabled={loading}
          >
            {fieldTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Enter field description"
            rows="3"
          />
        </div>

        <div className="form-group">
          <label htmlFor="defaultValue">Default Value</label>
          <input
            type="text"
            id="defaultValue"
            name="defaultValue"
            value={formData.defaultValue}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Enter default value (optional)"
          />
        </div>

        <div className="form-group">
          <label htmlFor="ordering">Display Order</label>
          <input
            type="number"
            id="ordering"
            name="ordering"
            value={formData.ordering}
            onChange={handleInputChange}
            disabled={loading}
            min="1"
            step="1"
          />
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="isRequired"
              checked={formData.isRequired}
              onChange={handleInputChange}
              disabled={loading}
            />
            Required Field
          </label>
        </div>

        <div className="form-actions" style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <button 
            type="submit" 
            disabled={loading || !formData.name.trim()}
            style={{ opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Saving...' : (isEditing ? 'Update Field' : 'Add Field')}
          </button>
          
          <button 
            type="button" 
            className="secondary" 
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default EndeavorTypeMetadataEdit;